import React, { Component, ErrorInfo, ReactNode } from 'react';
import { UnifiedButton } from '@/components/ui/UnifiedButton';
import { AlertTriangle } from 'lucide-react';
import { reportErrorToTech } from '@/lib/error-reporter';
import { isChunkLoadError, recoverFromChunkLoad } from '@/utils/chunkReload';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Check if this is a chunk load failure (stale cache after deploy)
    // Auto-reload to get fresh assets instead of showing error UI
    if (isChunkLoadError(error.message)) {
      console.warn('Chunk load failed, auto-reloading for fresh assets');
      recoverFromChunkLoad();
      return;
    }

    // Automatically report to tech team
    reportErrorToTech({
      errorMessage: error.message,
      stackTrace: error.stack || undefined,
      componentStack: errorInfo.componentStack || undefined,
      source: 'error_boundary',
      additionalContext: {
        errorName: error.name,
      },
    }).catch(() => {
      // Fail silently - don't break error boundary if reporting fails

      console.error('[ErrorBoundary] Failed to auto-report error');
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || 'Unknown error';
      const isDevelopment = import.meta.env.DEV;

      return (
        <div className="min-h-screen bg-glass-medium backdrop-blur-md flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <div className="bg-glass-dark border border-glass-medium rounded-[1.3rem] overflow-hidden p-6">
              {/* Header with Icon */}
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-[1.3rem] bg-amber-500/20 border border-amber-500/30">
                  <AlertTriangle className="w-6 h-6 text-amber-300" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-amber-300">Oops! We hit a small snag</h1>
                </div>
              </div>

              {/* Main Message */}
              <div className="space-y-4 text-white/80">
                <p>
                  Don&rsquo;t worry - this happens sometimes. We&rsquo;ve logged the issue and our
                  team will look into it.
                </p>
                <p className="text-sm text-white/60">
                  In the meantime, try refreshing the page. If the problem persists, feel free to
                  reach out to our support team.
                </p>

                {/* Technical Details (Dev Mode) */}
                {isDevelopment && errorMessage && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-amber-300 hover:text-amber-200 text-sm font-medium">
                      Technical details (dev mode)
                    </summary>
                    <div className="mt-3 p-4 bg-glass-light/20 border border-glass-medium rounded-[1.3rem]">
                      <pre className="text-xs text-amber-200/70 overflow-auto max-h-32 whitespace-pre-wrap break-words">
                        {errorMessage}
                      </pre>
                    </div>
                  </details>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 space-y-3">
                <UnifiedButton
                  onClick={() => window.location.reload()}
                  className="w-full gap-2 bg-cyber-cyan hover:bg-cyber-cyan/80 text-white"
                  data-testid="button-refresh-page"
                >
                  Refresh Page
                </UnifiedButton>
                <UnifiedButton
                  onClick={this.handleReset}
                  variant="link"
                  className="w-full text-cyber-cyan hover:text-cyber-cyan/80 text-sm py-2 transition-colors"
                  data-testid="button-try-again"
                >
                  Try Again Without Refresh
                </UnifiedButton>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
